import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { SEO } from "../components/SEO";
import odooApi from "../services/odooApi";

const FALLBACK_TITLE = "Privacy Policy";
const FALLBACK_SUBTITLE = "How we collect, use, and protect your information.";

export const PrivacyPolicyPage = () => {
  const [pageContent, setPageContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPage = async () => {
      try {
        const page = await odooApi.getPageContent("privacy");
        if (page) {
          setPageContent(page);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadPage();
  }, []);

  const title = pageContent?.title || FALLBACK_TITLE;
  const subtitle = pageContent?.subtitle || FALLBACK_SUBTITLE;
  const heroImage = pageContent?.hero_image_url || "";

  return (
    <>
      <SEO
        title={title}
        description={subtitle}
        keywords="privacy policy, data protection, personal information, Velaris privacy"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: FALLBACK_TITLE },
        ]}
      />

      <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
        <div className="relative overflow-hidden bg-black py-20 sm:py-24">
          {heroImage ? (
            <>
              <img
                src={heroImage}
                alt={title}
                className="absolute inset-0 h-full w-full object-cover opacity-30"
              />
              <div className="absolute inset-0 bg-black/75" />
            </>
          ) : (
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(212,175,55,0.28),_transparent_45%),linear-gradient(135deg,_#000_0%,_#111827_100%)]" />
          )}

          <div className="relative z-10 mx-auto flex max-w-5xl flex-col items-start px-4 sm:px-6 lg:px-8">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/40 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#D4AF37]">
              <ShieldCheck className="h-4 w-4" />
              Legal
            </div>
            <h1 className="font-[var(--font-display)] text-4xl font-bold text-white sm:text-5xl">
              {title}
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-white/70 sm:text-lg">
              {subtitle}
            </p>
          </div>
        </div>

        <section className="py-16 sm:py-20">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8 lg:p-10">
              {isLoading ? (
                <div className="space-y-4">
                  <div className="h-5 w-40 animate-pulse rounded bg-gray-200" />
                  <div className="h-5 w-full animate-pulse rounded bg-gray-100" />
                  <div className="h-5 w-5/6 animate-pulse rounded bg-gray-100" />
                  <div className="h-5 w-4/6 animate-pulse rounded bg-gray-100" />
                </div>
              ) : pageContent?.content ? (
                <div
                  className="text-gray-700 leading-8
                    [&_a]:font-medium [&_a]:text-[#B8860B] [&_a]:underline-offset-4 hover:[&_a]:text-[#8f6b00] hover:[&_a]:underline
                    [&_h1]:mb-6 [&_h1]:mt-10 [&_h1]:font-[var(--font-display)] [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-gray-950
                    [&_h2]:mb-5 [&_h2]:mt-10 [&_h2]:font-[var(--font-display)] [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-gray-950
                    [&_h3]:mb-4 [&_h3]:mt-8 [&_h3]:font-[var(--font-display)] [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-gray-900
                    [&_hr]:my-8 [&_hr]:border-gray-200
                    [&_li]:mb-3
                    [&_ol]:my-5 [&_ol]:list-decimal [&_ol]:space-y-3 [&_ol]:pl-6
                    [&_p]:mb-5
                    [&_strong]:font-semibold [&_strong]:text-gray-950
                    [&_ul]:my-5 [&_ul]:list-disc [&_ul]:space-y-3 [&_ul]:pl-6"
                  dangerouslySetInnerHTML={{ __html: pageContent.content }}
                />
              ) : (
                <div className="rounded-2xl border border-dashed border-[#D4AF37]/50 bg-[#D4AF37]/5 p-6 text-gray-700">
                  <h2 className="font-[var(--font-display)] text-2xl font-semibold text-gray-950">
                    Privacy policy content not published yet
                  </h2>
                  <p className="mt-3 leading-7">
                    Add or update the privacy policy from Odoo Dashboard &gt; Website CMS &gt; Page Content, then open this page again.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-10 flex flex-col gap-4 rounded-3xl bg-black px-6 py-8 text-white sm:flex-row sm:items-center sm:justify-between sm:px-8">
              <div>
                <p className="text-sm uppercase tracking-[0.22em] text-[#D4AF37]">
                  Need help?
                </p>
                <h2 className="mt-2 font-[var(--font-display)] text-2xl font-semibold">
                  Questions about your data or bookings?
                </h2>
              </div>

              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-sm bg-[#D4AF37] px-6 py-3 font-semibold text-black transition-colors hover:bg-[#c7a022]"
              >
                Contact Us
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
